import React, { useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Paper,
  Stack,
  Alert,
  IconButton,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  AccessTime,
  Send,
  WhatsApp,
  Instagram,
  LinkedIn,
  Facebook,
  Twitter,
} from '@mui/icons-material';

// Theme color shades
const themeColors = {
  primary: '#9b4d3c',      // Dark brown/red
  primaryLight: '#b86b5a',  // Medium brown/red
  secondary: '#d49b7b',     // Light brown/tan
  secondaryLight: '#e5b8a0', // Very light brown/tan
  accent1: '#8a3f2f',       // Darker shade
  accent2: '#c47a5a',       // Medium shade
  accent3: '#dfb195',       // Light shade
  accent4: '#f0d5c5',       // Very light shade
  accent5: '#7a3b2e',       // Darkest shade
  accent6: '#eac7b5',       // Lightest shade
};

// Section border style - matching homepage and other pages
const sectionBorderStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '4px',
  background: 'linear-gradient(90deg, #9b4d3c 0%, #d49b7b 50%, #e0e0e0 100%)',
  zIndex: 1,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

// Title style with moving underline
const titleWithUnderlineStyle = {
  fontWeight: 600,
  color: '#333',
  fontSize: { xs: '2.2rem', md: '2.8rem' },
  position: 'relative',
  display: 'inline-block',
  pb: 2,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 80,
    height: 3,
    background: 'linear-gradient(90deg, #9b4d3c, #d49b7b)',
    borderRadius: 2,
    animation: 'pulseWidth 2s ease-in-out infinite',
  }
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    });
    setTimeout(() => setSubmitted(false), 5000);
  };

  const contactInfo = [
    { 
      icon: <Phone />, 
      title: 'Phone', 
      details: '+1 (234) 567-890', 
      subdetails: 'Mon-Fri 9am-6pm', 
      color: themeColors.primary,
      gradient: 'linear-gradient(135deg, #9b4d3c 0%, #b86b5a 100%)'
    },
    { 
      icon: <Email />, 
      title: 'Email', 
      details: 'info@marvelous.com', 
      subdetails: '24/7 support', 
      color: themeColors.primaryLight,
      gradient: 'linear-gradient(135deg, #b86b5a 0%, #d49b7b 100%)'
    },
    { 
      icon: <LocationOn />, 
      title: 'Office', 
      details: '123 Business Ave, Suite 100', 
      subdetails: 'New York, NY 10001', 
      color: themeColors.accent1,
      gradient: 'linear-gradient(135deg, #8a3f2f 0%, #b86b5a 100%)'
    },
    { 
      icon: <AccessTime />, 
      title: 'Business Hours', 
      details: 'Mon-Fri: 9:00 AM - 6:00 PM', 
      subdetails: 'Sat-Sun: Closed', 
      color: themeColors.secondary,
      gradient: 'linear-gradient(135deg, #d49b7b 0%, #e5b8a0 100%)'
    },
  ];

  const socialMedia = [
    { icon: <WhatsApp />, name: 'WhatsApp', link: 'https://wa.me/1234567890', gradient: 'linear-gradient(135deg, #9b4d3c 0%, #b86b5a 100%)' },
    { icon: <Instagram />, name: 'Instagram', link: 'https://instagram.com/marvelous', gradient: 'linear-gradient(135deg, #b86b5a 0%, #d49b7b 100%)' },
    { icon: <LinkedIn />, name: 'LinkedIn', link: 'https://linkedin.com/company/marvelous', gradient: 'linear-gradient(135deg, #8a3f2f 0%, #b86b5a 100%)' },
    { icon: <Facebook />, name: 'Facebook', link: 'https://facebook.com/marvelous', gradient: 'linear-gradient(135deg, #d49b7b 0%, #e5b8a0 100%)' },
    { icon: <Twitter />, name: 'Twitter', link: 'https://twitter.com/marvelous', gradient: 'linear-gradient(135deg, #9b4d3c 0%, #d49b7b 100%)' },
  ];

  return (
    <Box sx={{ bgcolor: '#faf7f2', minHeight: '100vh' }}>
      {/* SECTION 1: HEADER - With moving underline */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Fade in={true} timeout={800}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="overline" 
                sx={{ 
                  color: '#9b4d3c', 
                  fontSize: '0.9rem', 
                  letterSpacing: 4,
                  fontWeight: 500,
                  display: 'block',
                  mb: 1,
                }}
              >
                GET IN TOUCH
              </Typography>
              <Typography 
                variant="h2" 
                component="h1" 
                sx={titleWithUnderlineStyle}
              >
                Contact Us
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ 
                  maxWidth: 600, 
                  mx: 'auto',
                  mt: 4,
                }}
              >
                Get in touch with our team for any inquiries about our real estate and hospitality services
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* SECTION 2: CONTACT INFORMATION & FORM - With thicker gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          <Grid container spacing={4}>
            {/* Contact Information */}
            {/* <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={3}>
                {contactInfo.map((item, index) => (
                  <Zoom in={true} timeout={1000 + index * 200} key={index}>
                    <Paper 
                      sx={{ 
                        p: 3, 
                        display: 'flex', 
                        alignItems: 'center',
                        transition: 'transform 0.3s, box-shadow 0.3s, border-color 0.3s',
                        borderRadius: 3,
                        border: '2px solid #9b4d3c',
                        boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                        '&:hover': {
                          transform: 'translateX(5px)',
                          boxShadow: '0 15px 30px rgba(155,77,60,0.15)',
                          borderColor: '#d49b7b',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          background: item.gradient,
                          color: 'white',
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          transition: 'transform 0.3s',
                          '&:hover': {
                            transform: 'scale(1.1) rotate(5deg)',
                          },
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: '#333' }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body1" sx={{ color: item.color, fontWeight: 600 }}>
                          {item.details}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.subdetails}
                        </Typography>
                      </Box>
                    </Paper>
                  </Zoom>
                ))}
                

                <Zoom in={true} timeout={1800}>
                  <Paper 
                    sx={{ 
                      p: 3,
                      borderRadius: 3,
                      border: '2px solid #d49b7b',
                      boxShadow: '0 5px 20px rgba(155,77,60,0.05)',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 15px 30px rgba(155,77,60,0.15)',
                      },
                    }}
                  >
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      align="center"
                      sx={{ 
                        fontWeight: 600,
                        color: '#9b4d3c',
                      }}
                    >
                      Follow Us
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                      {socialMedia.map((social, index) => (
                        <IconButton
                          key={index}
                          component="a"
                          href={social.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            background: social.gradient,
                            color: 'white',
                            width: 45,
                            height: 45,
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                              transform: 'scale(1.1) rotate(5deg)',
                              boxShadow: '0 10px 20px rgba(155,77,60,0.3)',
                            },
                          }}
                        >
                          {social.icon}
                        </IconButton>
                      ))}
                    </Box>
                  </Paper>
                </Zoom>
              </Stack>
            </Grid> */}

            <Grid container spacing={3}>
              {contactInfo.map((item, index) => (
                <Grid item xs={12} md key={index}>
                  <Zoom in={true} timeout={1000 + index * 200}>
                    <Paper
                      sx={{
                        height: "100%",
                        p: 3,
                        display: "flex",
                        alignItems: "center",
                        borderRadius: 3,
                        border: "2px solid #9b4d3c",
                        boxShadow: "0 5px 20px rgba(0,0,0,0.05)",
                        transition:
                          "transform 0.3s, box-shadow 0.3s, border-color 0.3s",
                        "&:hover": {
                          transform: "translateY(-6px)",
                          boxShadow: "0 15px 30px rgba(155,77,60,0.15)",
                          borderColor: "#d49b7b",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          background: item.gradient,
                          color: "white",
                          width: 50,
                          height: 50,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                        }}
                      >
                        {item.icon}
                      </Box>

                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {item.title}
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{ color: item.color, fontWeight: 600 }}
                        >
                          {item.details}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          {item.subdetails}
                        </Typography>
                      </Box>
                    </Paper>
                  </Zoom>
                </Grid>
              ))}

              {/* Social Media Section */}
              <Grid item xs={12} md>
                <Zoom in={true} timeout={1800}>
                  <Paper
                    sx={{
                      height: "100%",
                      p: 3,
                      borderRadius: 3,
                      border: "2px solid #d49b7b",
                      boxShadow: "0 5px 20px rgba(155,77,60,0.05)",
                      transition: "transform 0.3s, box-shadow 0.3s",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow: "0 15px 30px rgba(155,77,60,0.15)",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      align="center"
                      sx={{ fontWeight: 600, color: "#9b4d3c" }}
                    >
                      Follow Us
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      {socialMedia.map((social, index) => (
                        <IconButton
                          key={index}
                          component="a"
                          href={social.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            background: social.gradient,
                            color: "white",
                            width: 45,
                            height: 45,
                            transition: "transform 0.3s, box-shadow 0.3s",
                            "&:hover": {
                              transform: "scale(1.1)",
                              boxShadow:
                                "0 10px 20px rgba(155,77,60,0.3)",
                            },
                          }}
                        >
                          {social.icon}
                        </IconButton>
                      ))}
                    </Box>
                  </Paper>
                </Zoom>
              </Grid>
            </Grid>

            {/* Contact Form */}
            {/* <Grid item xs={12} md={8}>
              <Fade in={true} timeout={1500}>
                <Paper 
                  sx={{ 
                    p: 4,
                    borderRadius: 3,
                    border: '2px solid #d49b7b',
                    boxShadow: '0 5px 20px rgba(155,77,60,0.05)',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      boxShadow: '0 15px 30px rgba(155,77,60,0.15)',
                    },
                  }}
                >
                  <Typography 
                    variant="h4" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 600,
                      color: '#9b4d3c',
                    }}
                  >
                    Send us a Message
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    We'll get back to you within 24 hours
                  </Typography>

                  {submitted && (
                    <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                      Thank you for your message! We'll contact you soon.
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('name')}
                          onBlur={() => setFocusedField(null)}
                          required
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: focusedField === 'name' ? '#9b4d3c' : 'rgba(0,0,0,0.23)',
                                borderWidth: focusedField === 'name' ? '2px' : '1px',
                              },
                              '&:hover fieldset': {
                                borderColor: '#9b4d3c',
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('email')}
                          onBlur={() => setFocusedField(null)}
                          required
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: focusedField === 'email' ? '#9b4d3c' : 'rgba(0,0,0,0.23)',
                                borderWidth: focusedField === 'email' ? '2px' : '1px',
                              },
                              '&:hover fieldset': {
                                borderColor: '#9b4d3c',
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('phone')}
                          onBlur={() => setFocusedField(null)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: focusedField === 'phone' ? '#9b4d3c' : 'rgba(0,0,0,0.23)',
                                borderWidth: focusedField === 'phone' ? '2px' : '1px',
                              },
                              '&:hover fieldset': {
                                borderColor: '#9b4d3c',
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('subject')}
                          onBlur={() => setFocusedField(null)}
                          required
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: focusedField === 'subject' ? '#9b4d3c' : 'rgba(0,0,0,0.23)',
                                borderWidth: focusedField === 'subject' ? '2px' : '1px',
                              },
                              '&:hover fieldset': {
                                borderColor: '#9b4d3c',
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Message"
                          name="message"
                          multiline
                          rows={4}
                          value={formData.message}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('message')}
                          onBlur={() => setFocusedField(null)}
                          required
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: focusedField === 'message' ? '#9b4d3c' : 'rgba(0,0,0,0.23)',
                                borderWidth: focusedField === 'message' ? '2px' : '1px',
                              },
                              '&:hover fieldset': {
                                borderColor: '#9b4d3c',
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            endIcon={<Send />}
                            sx={{
                              background: 'linear-gradient(135deg, #9b4d3c 0%, #d49b7b 100%)',
                              px: 6,
                              py: 1.5,
                              fontSize: '1.1rem',
                              borderRadius: 2,
                              transition: 'transform 0.3s, box-shadow 0.3s',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 10px 20px rgba(155,77,60,0.3)',
                              },
                              '&:active': {
                                transform: 'translateY(0)',
                              },
                            }}
                          >
                            Send Message
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                </Paper>
              </Fade>
            </Grid> */}
          </Grid>
        </Container>
      </Box>

      {/* SECTION 3: MAP - With thicker gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          <Zoom in={true} timeout={1000}>
            <Paper 
              sx={{ 
                height: 400, 
                overflow: 'hidden',
                borderRadius: 3,
                border: '2px solid #9b4d3c',
                boxShadow: '0 5px 20px rgba(155,77,60,0.1)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'scale(1.01)',
                  boxShadow: '0 20px 40px rgba(155,77,60,0.2)',
                },
              }}
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193595.15830869428!2d-74.119763973046!3d40.69766374874431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY%2C%20USA!5e0!3m2!1sen!2s!4v1645564628768!5m2!1sen!2s"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Marvelous Location"
              />
            </Paper>
          </Zoom>
        </Container>
      </Box>

      {/* SECTION 4: FOOTER - With thicker gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Experience the Marvellous difference
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Add keyframe animation for the moving underline */}
      <style>
        {`
          @keyframes pulseWidth {
            0%, 100% { width: 80px; }
            50% { width: 120px; }
          }
        `}
      </style>
    </Box>
  );
};

export default ContactPage;